import type { ReactNode } from 'react';
import type { Exam } from '../../../../../db/exam.js';
import { ownMaxPoints } from '../../../../../db/utils/points-model.js';
import { type BoundingBoxContentType } from '../../../../../shared/bounding-box-content.js';
import {
  findLanguageUseRuleResultsForTask,
  type LanguageUseRuleResultLike,
  type LanguageUseSchemeLike,
} from '../../../../../shared/grammar-rules-resolution.js';
import { GRADING_THRESHOLDS } from '../../../../lib/constants.js';

// --- Interfaces ---
export interface UnifiedBoundingBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  ocrExtractedText: string;
  contentType?: BoundingBoxContentType;
  confidence?: number;
  approved?: boolean;
  /** True while this box's OCR is actively re-running server-side — see BoundingBox.ocrRerunning. */
  ocrRerunning?: boolean;
}

export interface GradingResult {
  score: number;
  explanation: string;
  confidence?: number | string;
  reviewerFeedback?: string;
  isApproved?: boolean;
  actualPointsGiven?: number;
  /** Provenance of `score`/`explanation` — see db/submission.ts's GradingResult.gradingSource. */
  gradingSource?: 'ai_first_pass' | 'ai_consistency_adjusted' | 'manual';
}

/** A grammar/spelling error positioned on the student image (percentages 0–100, pageNumber 0-based). */
export interface GrammarErrorView {
  id: string;
  message: string;
  shortMessage?: string;
  ruleId: string;
  category: string;
  context?: string;
  // null when the error could not be located on the image — listed in the card list without underline.
  box: { x: number; y: number; width: number; height: number; pageNumber: number } | null;
  isManual?: boolean;
}

/** A manually-created grammar error (teacher annotation). */
export interface ManualGrammarError {
  _id: string;
  category: string;
  message: string;
  box: { x: number; y: number; width: number; height: number; pageNumber: number };
}

export interface GrammarEvaluationView {
  status?: string;
  errorCount: number;
  wordCount: number;
  fehlerindex: number;
  errors: GrammarErrorView[];
}

export interface SubSubmissionTask {
  _id?: string;
  taskNumber: string;
  subTasksSubmission?: SubSubmissionTask[];
  boundingBoxes?: UnifiedBoundingBox[];
  gradingResult?: GradingResult;
  grammarEvaluation?: GrammarEvaluationView;
}

export interface MainSubmissionTask {
  _id?: string;
  taskNumber: string;
  subTasksSubmission?: SubSubmissionTask[];
  boundingBoxes?: UnifiedBoundingBox[];
  gradingResult?: GradingResult;
  grammarEvaluation?: GrammarEvaluationView;
}

export interface Submission {
  _id: string;
  studentName?: string;
  pages: string[];
  /**
   * Answered on the student exam page instead of on paper — see `Submission.answersSubmittedOnline`.
   * Every box on it carries the student's own typed words, so there is no transcription to show, correct
   * or approve: the whole extracted-text panel is suppressed for these.
   */
  answersSubmittedOnline?: boolean;
  taskSubmissions?: MainSubmissionTask[];
  languageUseRuleResults?: LanguageUseRuleResultLike[];
}

export interface FlatSubTaskEntry {
  task: SubSubmissionTask;
  flatIndex: number;
  depth: number;
  numberPath: string[];
}

export const flattenSubTasksWithMeta = (mainTask: MainSubmissionTask | undefined): FlatSubTaskEntry[] => {
  if (!mainTask?.subTasksSubmission?.length) return [];

  const out: FlatSubTaskEntry[] = [];
  let flatIndex = 0;

  const walk = (tasks: SubSubmissionTask[] | undefined, depth: number, parentPath: string[]) => {
    if (!tasks?.length) return;

    for (const task of tasks) {
      const taskNumber = String(task?.taskNumber ?? '').trim() || '?';
      const numberPath = [...parentPath, taskNumber];
      out.push({ task, flatIndex, depth, numberPath });
      flatIndex += 1;
      walk(task.subTasksSubmission, depth + 1, numberPath);
    }
  };

  walk(mainTask.subTasksSubmission, 1, []);
  return out;
};

export const getSubTaskByFlatIndex = (
  mainTask: MainSubmissionTask | undefined,
  flatIndex: number | null,
): SubSubmissionTask | null => {
  if (flatIndex === null || flatIndex < 0) return null;
  const entry = flattenSubTasksWithMeta(mainTask).find(item => item.flatIndex === flatIndex);
  return entry?.task ?? null;
};

/**
 * Discriminates which entity a grading-canvas save targets: plain content, or a LanguageUseScheme (by
 * id) — solo (1-member) or multi-member alike, both resolve into Submission.languageUseRuleResults — see
 * grammar-rules-resolution.ts module doc for why a task can belong to more than one rule at once.
 */
export type SaveTarget = { kind: 'content' } | { kind: 'group'; languageUseSchemeId: string };

export interface CombinedCorrectionProps {
  submissions: Submission[];
  exam: Exam | null;
  mode?: 'answers' | 'correction' | 'grammar';
  onSaveUpdate: (
    submissionId: string,
    mainTaskId: string,
    subTaskId: string | null,
    boxId: string | null,
    updatedData: {
      extractedText?: string;
      gradingText?: string;
      score?: number;
      isApproved?: boolean;
      boxApproved?: boolean;
      boxCoordinates?: Partial<UnifiedBoundingBox>;
      addBoundingBox?: boolean;
      deleteBoundingBox?: boolean;
      rerunOcr?: boolean;
      languageUseScore?: number;
      languageUseExplanation?: string;
      languageUseIsApproved?: boolean;
      languageUseReviewerFeedback?: string;
      target?: SaveTarget;
    },
  ) => Promise<void>;
  onDeleteGrammarError?: (submissionId: string, mainTaskId: string, errorId: string) => Promise<void>;
  onAddGrammarError?: (submissionId: string, mainTaskId: string, error: ManualGrammarError) => Promise<boolean>;
  onUpdateGrammarError?: (
    submissionId: string,
    mainTaskId: string,
    errorId: string,
    category: string,
    message: string,
    box: { x: number; y: number; width: number; height: number; pageNumber: number },
  ) => Promise<boolean>;
  initialSubmissionIdx?: number;
  initialTaskKey?: string;
  /** Seeds the per-box content/language-use toggle when opening directly onto a task (e.g. from the
   * summary canvas's clarification list) whose only open issue is language-use-side. */
  initialBoxMode?: 'content' | 'languageUse';
  onOpenCanvas?: (view: 'boundingbox' | 'correction' | 'msedit' | 'summary' | 'submissioncheck') => void;
  focusFirstLowConfidence?: boolean;
  focusFirstLowConfidenceMode?: 'any' | 'bbx';
  onHeaderContent?: (content: ReactNode | null) => void;
  /** Reports the task/student currently visible in this canvas so the chat agent can be told about
   * it — purely informational context, never a target for MS/GS edits (those still require an
   * explicit task number from the teacher). */
  onVisibleTaskChange?: (info: { taskNumber: string | null; studentId: string | null; studentName: string | null }) => void;
  /** Task ids currently being (re)generated by a live gsExtraction/gsGeneration/gsRefine(target: 'content'
   * |absent) job — drives the read-only GradingSchemePanel spinner echoed into this view in content mode.
   * Same Set the ms-gs canvas uses (see SolutionGradingCheckProps.refiningGradingSchemeTaskIds), sourced
   * from the same subscription. */
  refiningGradingSchemeTaskIds?: Set<string>;
  /** Same sourcing, for gsRefine(target: 'languageUse') (plus gsExtraction/gsGeneration) — drives the
   * echoed panel's spinner in languageUse mode. Kept separate so content and languageUse refines can
   * never light up each other's box — see SolutionGradingCheckProps.refiningGradingSchemeLanguageUseTaskIds. */
  refiningGradingSchemeLanguageUseTaskIds?: Set<string>;
  /** `${submissionId}::${taskKey}` keys with a live `grading` (content) job — drives the AiGradingPanel
   * spinner in content mode. See streamLiveGradingJobsByExamIdSubscription. */
  regradingContentKeys?: Set<string>;
  /** `${submissionId}::${languageUseSchemeId}` keys with a live languageUse grading job — drives the
   * AiGradingPanel spinner in languageUse mode. */
  regradingLanguageUseKeys?: Set<string>;
  /** The subset of `regradingContentKeys` whose rows are all still QUEUED — nothing is running for them yet.
   * Only changes the spinner's wording; a queued box is still busy and still must not be edited. */
  queuedContentKeys?: Set<string>;
  /** The subset of `regradingLanguageUseKeys` whose rows are all still queued. */
  queuedLanguageUseKeys?: Set<string>;
}

export type ConfidenceLevel = 'none' | 'medium' | 'low';

// --- Helper Functions ---
export const hasLowConfidenceSignal = (
  gradingResult: GradingResult | undefined,
  boxes: UnifiedBoundingBox[] | undefined,
): boolean => getConfidenceLevelSignal(gradingResult, boxes) === 'low';

export const getConfidenceLevelSignal = (
  gradingResult: GradingResult | undefined,
  boxes: UnifiedBoundingBox[] | undefined,
): ConfidenceLevel => {
  if (gradingResult?.isApproved === true) return 'none';

  const candidates: number[] = [];

  const gradingConfidence = Number(gradingResult?.confidence);
  if (Number.isFinite(gradingConfidence)) candidates.push(gradingConfidence);

  if (Array.isArray(boxes)) {
    for (const box of boxes) {
      if ((box as any)?.approved === true) continue;
      const confidence = Number((box as any)?.confidence);
      if (Number.isFinite(confidence)) candidates.push(confidence);
    }
  }

  if (candidates.length === 0) return 'none';
  const minConfidence = Math.min(...candidates);
  if (minConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE) return 'low';
  if (minConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
  return 'none';
};

export const hasMediumConfidenceSignal = (
  gradingResult: GradingResult | undefined,
  boxes: UnifiedBoundingBox[] | undefined,
): boolean => getConfidenceLevelSignal(gradingResult, boxes) !== 'none';
export interface LanguageUseConfidenceSource {
  confidence?: number | string | null;
  isApproved?: boolean | null;
  /** The covering LanguageUseScheme's _id — lets callers dedupe the same underlying decision when a
   * multi-member group is linked to several tasks (dependsOn). */
  schemeId: string;
}

/**
 * Dual-signal counterpart to getConfidenceLevelSignal: a task can be linked to more than one
 * LanguageUseScheme at once, so this folds the same low/medium/none logic over every applicable
 * language-use source instead of a single gradingResult. Kept independent of content confidence —
 * see the box-mode toggle's secondary dot, which never blends into one color.
 */
export const getLanguageUseConfidenceLevelSignal = (sources: LanguageUseConfidenceSource[]): ConfidenceLevel => {
  const candidates: number[] = [];
  for (const source of sources) {
    if (source?.isApproved === true) continue;
    const confidence = Number(source?.confidence);
    if (Number.isFinite(confidence)) candidates.push(confidence);
  }

  if (candidates.length === 0) return 'none';
  const minConfidence = Math.min(...candidates);
  if (minConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE) return 'low';
  if (minConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
  return 'none';
};

export const parseScore = (raw: unknown): number | null => {
  const parsed =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw.replace(',', '.').replace(/[^0-9.\-]/g, ''))
        : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

export const findUncertainties = (submissions: Submission[]) => {
  const issues: Array<{
    submissionIdx: number;
    mainTaskIdx: number;
    subTaskIdx: number | null;
    level: ConfidenceLevel;
  }> = [];

  submissions.forEach((submission, subIdx) => {
    submission.taskSubmissions?.forEach((mainTask, mainIdx) => {
      const mainGradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      const mainLevel = getConfidenceLevelSignal(mainGradingResult, mainTask.boundingBoxes);
      if (mainLevel !== 'none') {
        issues.push({ submissionIdx: subIdx, mainTaskIdx: mainIdx, subTaskIdx: null, level: mainLevel });
      }

      const flatSubTasks = flattenSubTasksWithMeta(mainTask);
      flatSubTasks.forEach(entry => {
        const gradingResult = ((entry.task.gradingResult as any)?.doc || entry.task.gradingResult) as
          | GradingResult
          | undefined;
        const level = getConfidenceLevelSignal(gradingResult, entry.task.boundingBoxes);
        if (level === 'none') return;
        issues.push({ submissionIdx: subIdx, mainTaskIdx: mainIdx, subTaskIdx: entry.flatIndex, level });
      });
    });
  });

  issues.sort((a, b) => {
    if (a.mainTaskIdx !== b.mainTaskIdx) return a.mainTaskIdx - b.mainTaskIdx;
    const aSub = a.subTaskIdx === null ? -1 : a.subTaskIdx;
    const bSub = b.subTaskIdx === null ? -1 : b.subTaskIdx;
    if (aSub !== bSub) return aSub - bSub;
    return a.submissionIdx - b.submissionIdx;
  });

  return issues.map(({ submissionIdx, mainTaskIdx, subTaskIdx }) => ({ submissionIdx, mainTaskIdx, subTaskIdx }));
};

const languageUseConfidenceSourcesForTask = (
  taskNumber: string | null | undefined,
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
  languageUseRuleResults: LanguageUseRuleResultLike[] | null | undefined,
): LanguageUseConfidenceSource[] =>
  findLanguageUseRuleResultsForTask(taskNumber, languageUseSchemeRules, languageUseRuleResults).map(result => ({
    confidence: result.confidence,
    isApproved: result.isApproved,
    schemeId: result.languageUseSchemeId,
  }));

/** Sources that actually contribute an unresolved low/medium confidence signal (mirrors the filter
 * inside getLanguageUseConfidenceLevelSignal), so callers can tell WHICH sources drove a task's issue. */
const unresolvedConfidenceSources = (sources: LanguageUseConfidenceSource[]): LanguageUseConfidenceSource[] =>
  sources.filter(source => {
    if (source?.isApproved === true) return false;
    const confidence = Number(source?.confidence);
    return Number.isFinite(confidence) && confidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;
  });

/**
 * Every (submission, task) combo with a non-'none' language-use confidence signal — every
 * LanguageUseScheme result the task is linked to, folded per task via getLanguageUseConfidenceLevelSignal.
 * Shared base for findLanguageUseUncertainties (navigation) and
 * getLanguageUseConfidenceLevelForTaskAcrossSubmissions (dropdown/step row coloring) so both agree.
 * Each issue also carries `schemeIds` — a multi-member group result linked to several tasks
 * (dependsOn) surfaces once per member task here (needed so every affected task still gets colored),
 * but findLanguageUseUncertainties uses this field to dedupe it back down to a single
 * navigable/countable uncertainty per distinct decision.
 */
const findLanguageUseConfidenceIssues = (
  submissions: Submission[],
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): Array<{
  submissionIdx: number;
  mainTaskIdx: number;
  subTaskIdx: number | null;
  level: ConfidenceLevel;
  schemeIds: string[];
}> => {
  const issues: Array<{
    submissionIdx: number;
    mainTaskIdx: number;
    subTaskIdx: number | null;
    level: ConfidenceLevel;
    schemeIds: string[];
  }> = [];

  const dedupeInfo = (sources: LanguageUseConfidenceSource[]) => ({
    schemeIds: Array.from(new Set(unresolvedConfidenceSources(sources).map(s => s.schemeId))),
  });

  submissions.forEach((submission, subIdx) => {
    submission.taskSubmissions?.forEach((mainTask, mainIdx) => {
      const mainSources = languageUseConfidenceSourcesForTask(
        mainTask.taskNumber,
        languageUseSchemeRules,
        submission.languageUseRuleResults,
      );
      const mainLevel = getLanguageUseConfidenceLevelSignal(mainSources);
      if (mainLevel !== 'none') {
        issues.push({
          submissionIdx: subIdx,
          mainTaskIdx: mainIdx,
          subTaskIdx: null,
          level: mainLevel,
          ...dedupeInfo(mainSources),
        });
      }

      const flatSubTasks = flattenSubTasksWithMeta(mainTask);
      flatSubTasks.forEach(entry => {
        // "|"-joined full ancestor path (main task + numberPath) — the same qualified reference
        // LanguageUseScheme.dependsOn now stores, not just this node's bare (ambiguous) taskNumber.
        const qualifiedTaskNumber = [String(mainTask.taskNumber ?? ''), ...entry.numberPath].join('|');
        const sources = languageUseConfidenceSourcesForTask(
          qualifiedTaskNumber,
          languageUseSchemeRules,
          submission.languageUseRuleResults,
        );
        const level = getLanguageUseConfidenceLevelSignal(sources);
        if (level === 'none') return;
        issues.push({
          submissionIdx: subIdx,
          mainTaskIdx: mainIdx,
          subTaskIdx: entry.flatIndex,
          level,
          ...dedupeInfo(sources),
        });
      });
    });
  });

  return issues;
};

/**
 * Language-use counterpart to findUncertainties, navigable independently (own second nav button) —
 * folds every LanguageUseScheme result a task is linked to (via
 * languageUseSchemeRules/submission.languageUseRuleResults) into one signal per task, since a task
 * can be linked to more than one at once. A multi-member group result linked to several tasks
 * (dependsOn) is one underlying decision, so it's deduped down to a single entry here — anchored to
 * whichever linked task is encountered first — even though findLanguageUseConfidenceIssues itself
 * still reports it once per linked task (needed for per-task coloring).
 */
export const findLanguageUseUncertainties = (
  submissions: Submission[],
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
) => {
  const issues = findLanguageUseConfidenceIssues(submissions, languageUseSchemeRules);

  issues.sort((a, b) => {
    if (a.mainTaskIdx !== b.mainTaskIdx) return a.mainTaskIdx - b.mainTaskIdx;
    const aSub = a.subTaskIdx === null ? -1 : a.subTaskIdx;
    const bSub = b.subTaskIdx === null ? -1 : b.subTaskIdx;
    if (aSub !== bSub) return aSub - bSub;
    return a.submissionIdx - b.submissionIdx;
  });

  const seenSchemeKeys = new Set<string>();
  const deduped = issues.filter(issue => {
    const hasNewScheme = issue.schemeIds.some(id => !seenSchemeKeys.has(`${issue.submissionIdx}:${id}`));
    issue.schemeIds.forEach(id => seenSchemeKeys.add(`${issue.submissionIdx}:${id}`));
    return hasNewScheme;
  });

  return deduped.map(({ submissionIdx, mainTaskIdx, subTaskIdx }) => ({ submissionIdx, mainTaskIdx, subTaskIdx }));
};

/**
 * Per-task worst language-use confidence level across every submission — feeds the task-dropdown row
 * coloring (and any other "does this task have language-use uncertainty anywhere" check) so it agrees
 * with findLanguageUseUncertainties instead of silently only reflecting content confidence.
 */
export const getLanguageUseConfidenceLevelForTaskAcrossSubmissions = (
  submissions: Submission[],
  languageUseSchemeRules: LanguageUseSchemeLike[] | null | undefined,
): Map<string, ConfidenceLevel> => {
  const map = new Map<string, ConfidenceLevel>();
  for (const issue of findLanguageUseConfidenceIssues(submissions, languageUseSchemeRules)) {
    const key = `${issue.mainTaskIdx}-${issue.subTaskIdx ?? 'main'}`;
    if (map.get(key) === 'low') continue;
    map.set(key, issue.level);
  }
  return map;
};

export const findBbxUncertainties = (submissions: Submission[]) => {
  const issues: Array<{ submissionIdx: number; mainTaskIdx: number; subTaskIdx: number | null }> = [];

  const resolveBoxesLevel = (boxes: UnifiedBoundingBox[] | undefined): ConfidenceLevel => {
    if (!Array.isArray(boxes) || boxes.length === 0) return 'none';
    const unresolvedConfidences = boxes
      .filter(box => (box as any)?.approved !== true)
      .map(box => Number((box as any)?.confidence))
      .filter((confidence): confidence is number => Number.isFinite(confidence));

    if (unresolvedConfidences.length === 0) return 'none';
    const minConfidence = Math.min(...unresolvedConfidences);
    if (minConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE) return 'low';
    if (minConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
    return 'none';
  };

  submissions.forEach((submission, submissionIdx) => {
    submission.taskSubmissions?.forEach((mainTask, mainTaskIdx) => {
      const mainLevel = resolveBoxesLevel(mainTask.boundingBoxes);
      if (mainLevel !== 'none') {
        issues.push({ submissionIdx, mainTaskIdx, subTaskIdx: null });
      }

      const flatSubTasks = flattenSubTasksWithMeta(mainTask);
      flatSubTasks.forEach(entry => {
        const level = resolveBoxesLevel(entry.task.boundingBoxes);
        if (level === 'none') return;
        issues.push({ submissionIdx, mainTaskIdx, subTaskIdx: entry.flatIndex });
      });
    });
  });

  issues.sort((a, b) => {
    if (a.mainTaskIdx !== b.mainTaskIdx) return a.mainTaskIdx - b.mainTaskIdx;
    const aSub = a.subTaskIdx === null ? -1 : a.subTaskIdx;
    const bSub = b.subTaskIdx === null ? -1 : b.subTaskIdx;
    if (aSub !== bSub) return aSub - bSub;
    return a.submissionIdx - b.submissionIdx;
  });

  return issues;
};

export const hasLowConfidenceForTaskAcrossSubmissions = (
  submissions: Submission[],
  mainTaskIdx: number,
  subTaskIdx: number | null,
  mode: 'answers' | 'correction' = 'correction',
): boolean =>
  submissions.some(submission => {
    const mainTask = submission.taskSubmissions?.[mainTaskIdx];
    if (!mainTask) return false;

    if (subTaskIdx === null) {
      const gradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      return mode === 'answers'
        ? hasLowConfidenceSignal(undefined, mainTask.boundingBoxes)
        : gradingResult?.isApproved !== true && hasLowConfidenceSignal(gradingResult, mainTask.boundingBoxes);
    }

    const subTask = getSubTaskByFlatIndex(mainTask, subTaskIdx);
    if (!subTask) return false;
    const gradingResult = ((subTask.gradingResult as any)?.doc || subTask.gradingResult) as GradingResult | undefined;
    return mode === 'answers'
      ? hasLowConfidenceSignal(undefined, subTask.boundingBoxes)
      : gradingResult?.isApproved !== true && hasLowConfidenceSignal(gradingResult, subTask.boundingBoxes);
  });

export const hasMediumConfidenceForTaskAcrossSubmissions = (
  submissions: Submission[],
  mainTaskIdx: number,
  subTaskIdx: number | null,
  mode: 'answers' | 'correction' = 'correction',
): boolean =>
  submissions.some(submission => {
    const mainTask = submission.taskSubmissions?.[mainTaskIdx];
    if (!mainTask) return false;

    if (subTaskIdx === null) {
      const gradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      return mode === 'answers'
        ? getConfidenceLevelSignal(undefined, mainTask.boundingBoxes) !== 'none'
        : getConfidenceLevelSignal(gradingResult, mainTask.boundingBoxes) !== 'none';
    }

    const subTask = getSubTaskByFlatIndex(mainTask, subTaskIdx);
    if (!subTask) return false;
    const gradingResult = ((subTask.gradingResult as any)?.doc || subTask.gradingResult) as GradingResult | undefined;
    return mode === 'answers'
      ? getConfidenceLevelSignal(undefined, subTask.boundingBoxes) !== 'none'
      : getConfidenceLevelSignal(gradingResult, subTask.boundingBoxes) !== 'none';
  });

export const getFirstSubmissionIndexWithLowConfidenceForTask = (
  submissions: Submission[],
  mainTaskIdx: number,
  subTaskIdx: number | null,
  mode: 'answers' | 'correction' = 'correction',
): number =>
  submissions.findIndex(submission => {
    const mainTask = submission.taskSubmissions?.[mainTaskIdx];
    if (!mainTask) return false;

    if (subTaskIdx === null) {
      const gradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      return mode === 'answers'
        ? hasLowConfidenceSignal(undefined, mainTask.boundingBoxes)
        : gradingResult?.isApproved !== true && hasLowConfidenceSignal(gradingResult, mainTask.boundingBoxes);
    }

    const subTask = getSubTaskByFlatIndex(mainTask, subTaskIdx);
    if (!subTask) return false;
    const gradingResult = ((subTask.gradingResult as any)?.doc || subTask.gradingResult) as GradingResult | undefined;
    return mode === 'answers'
      ? hasLowConfidenceSignal(undefined, subTask.boundingBoxes)
      : gradingResult?.isApproved !== true && hasLowConfidenceSignal(gradingResult, subTask.boundingBoxes);
  });

export const getFirstSubmissionIndexWithMediumConfidenceForTask = (
  submissions: Submission[],
  mainTaskIdx: number,
  subTaskIdx: number | null,
  mode: 'answers' | 'correction' = 'correction',
): number =>
  submissions.findIndex(submission => {
    const mainTask = submission.taskSubmissions?.[mainTaskIdx];
    if (!mainTask) return false;

    if (subTaskIdx === null) {
      const gradingResult = ((mainTask.gradingResult as any)?.doc || mainTask.gradingResult) as
        | GradingResult
        | undefined;
      return mode === 'answers'
        ? getConfidenceLevelSignal(undefined, mainTask.boundingBoxes) !== 'none'
        : getConfidenceLevelSignal(gradingResult, mainTask.boundingBoxes) !== 'none';
    }

    const subTask = getSubTaskByFlatIndex(mainTask, subTaskIdx);
    if (!subTask) return false;
    const gradingResult = ((subTask.gradingResult as any)?.doc || subTask.gradingResult) as GradingResult | undefined;
    return mode === 'answers'
      ? getConfidenceLevelSignal(undefined, subTask.boundingBoxes) !== 'none'
      : getConfidenceLevelSignal(gradingResult, subTask.boundingBoxes) !== 'none';
  });

export const calculateMaxScore = (
  exam: Exam | null,
  currentMainTask: any,
  currentMainExamTask: any,
  currentSubTask: any,
  currentSubTaskIdx: number | null,
): number => {
  const findExamSubTaskRecursive = (tasks: any[] | undefined, targetSubTask: any): any | undefined => {
    if (!Array.isArray(tasks) || !targetSubTask) return undefined;
    const targetId = String(targetSubTask?._id ?? '').trim();
    const targetTaskNumber = String(targetSubTask?.taskNumber ?? '').trim();

    for (const task of tasks) {
      if (!task) continue;
      const candidateId = String(task?._id ?? '').trim();
      const candidateTaskNumber = String(task?.taskNumber ?? '').trim();

      if (targetId && candidateId && candidateId === targetId) return task;
      if (!targetId && targetTaskNumber && candidateTaskNumber === targetTaskNumber) return task;

      const nested = findExamSubTaskRecursive(task.subTasks, targetSubTask);
      if (nested) return nested;
    }

    return undefined;
  };

  const flattenExamSubTasksWithMeta = (
    tasks: any[] | undefined,
    depth = 0,
    flatIndexRef = { value: 0 },
  ): Array<{ task: any; flatIndex: number; depth: number }> => {
    if (!Array.isArray(tasks) || tasks.length === 0) return [];

    const out: Array<{ task: any; flatIndex: number; depth: number }> = [];

    for (const task of tasks) {
      if (!task) continue;
      out.push({ task, flatIndex: flatIndexRef.value, depth });
      flatIndexRef.value += 1;
      out.push(...flattenExamSubTasksWithMeta(task.subTasks, depth + 1, flatIndexRef));
    }

    return out;
  };

  const findExamSubTaskByFlatIndex = (tasks: any[] | undefined, flatIndex: number | null): any | undefined => {
    if (flatIndex === null || flatIndex < 0) return undefined;
    return flattenExamSubTasksWithMeta(tasks).find(entry => entry.flatIndex === flatIndex)?.task;
  };

  if (!exam?.tasks || !currentMainTask || !currentMainExamTask) return 0;

  // The node's OWN answer worth — what the AI can award for THIS box's content — not the whole-subtree
  // total: a hybrid answerable parent's children are graded in their own separate boxes, so including
  // them here would double-count. 0 when unpointed; no default-to-1.
  if (currentSubTaskIdx === null) {
    return ownMaxPoints(currentMainExamTask);
  }

  const matchingSubTask =
    findExamSubTaskRecursive(currentMainExamTask.subTasks, currentSubTask) ??
    findExamSubTaskByFlatIndex(currentMainExamTask.subTasks, currentSubTaskIdx);
  return ownMaxPoints(matchingSubTask);
};
